import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import Title from '../../components/global/Title'
import { useVerifyAdminToken } from '../../hooks/useVerifyAdminToken'
import DashboardGrid from './dashboard/DashboardGrid'
import { useDashboardConfig } from './dashboard/hooks/useDashboardConfig'
import { WidgetLayout } from './dashboard/types/dashboard.type'

const DashboardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
`

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 24px;
`

const LoadingSpinner = styled.div`
    width: 48px;
    height: 48px;
    border: 4px solid #e5e7eb;
    border-top-color: #b40000;
    border-radius: 50%;
    animation: spin 1s linear infinite;

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
`

const AdminDashboard = () => {
    const { t } = useTranslation('translation')
    const verifyToken = useVerifyAdminToken()
    const { config, isLoading, updateConfig } = useDashboardConfig()

    verifyToken()

    if (isLoading) {
        return (
            <div data-testid="admin-dashboard-container">
                <Title dataTestId="admin-dashboard-title">{t('admin.dashboard')}</Title>
                <LoadingContainer>
                    <LoadingSpinner />
                </LoadingContainer>
            </div>
        )
    }

    const enabledWidgets =
        config?.config?.widgets
            ?.filter((widget) => widget.enabled)
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
            .map((widget) => widget.id) || []

    const layouts = config?.config?.layouts || {}

    const handleLayoutChange = (newLayouts: Record<string, WidgetLayout[]>) => {
        if (config) {
            updateConfig({
                ...config.config,
                layouts: newLayouts,
            })
        }
    }

    return (
        <div data-testid="admin-dashboard-container">
            <DashboardHeader>
                <Title dataTestId="admin-dashboard-title">{t('admin.dashboard')}</Title>
            </DashboardHeader>

            <DashboardGrid enabledWidgets={enabledWidgets} layouts={layouts} onLayoutChange={handleLayoutChange} />
        </div>
    )
}

export default AdminDashboard
